import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLiveEvents } from "@/lib/useLiveEvents";

export const isUnacknowledgedCall = (item) => item.kind === "request" && item.status === "new";

// Owned by the dashboard, so navigating away from the queue never stops alerts.
export function useServiceQueueAlerts(accountId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const active = useRef(false);
  const request = useRef(null);
  const refresh = useCallback(async (force = true) => {
    if (!active.current || (request.current && !force)) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/operations/queue", { signal: controller.signal });
      if (!controller.signal.aborted && active.current) {
        setItems(data?.items || []);
        setError(false);
      }
    } catch (err) {
      if (!controller.signal.aborted && active.current) {
        // A connection failure must never turn pending calls into "all clear".
        if ([401, 403].includes(err.response?.status)) setItems([]);
        setError(true);
      }
    } finally {
      if (request.current === controller && active.current) {
        request.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    active.current = Boolean(accountId);
    setItems([]);
    setError(false);
    refresh();
    const timer = setInterval(() => refresh(false), 5000);
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    const offline = () => { if (active.current) setError(true); };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", offline);
    return () => {
      active.current = false;
      request.current?.abort();
      request.current = null;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", offline);
    };
  }, [accountId, refresh]);

  useLiveEvents((event) => {
    if (event?.type === "call_repeated") toast.warning(`Guest pressed again · ${event.press_count || 2} presses`, { duration: 8000 });
    if (event?.type === "hello" || event?.type === "new_call" || event?.type?.startsWith("call_")) refresh();
  });

  return { items, loading, error, refresh, newCallCount: items.filter(isUnacknowledgedCall).length };
}
